/**
 * Admin Order Detail Page
 * View and manage individual order details
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import Badge from '../../../components/ui/Badge';
import type { OrderWithItems } from '@phoenixbill/shared/types/order';

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Status update
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // Cancel order
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/orders/${orderId}`);
      setOrder(response.data.data);
      setNewStatus(response.data.data.status);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!order || newStatus === order.status) return;

    try {
      setActionLoading(true);
      await api.put(`/admin/orders/${orderId}/status`, {
        status: newStatus,
        adminNotes: adminNotes || undefined,
      });
      await loadOrder();
      setShowStatusUpdate(false);
      setAdminNotes('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptFraudOrder = async () => {
    if (!confirm('Accept this fraud-flagged order?')) return;

    try {
      setActionLoading(true);
      await api.post(`/admin/orders/${orderId}/accept`, {
        adminNotes: 'Manually reviewed and accepted',
      });
      await loadOrder();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to accept order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }

    try {
      setActionLoading(true);
      await api.post(`/admin/orders/${orderId}/cancel`, {
        reason: cancelReason,
      });
      await loadOrder();
      setShowCancelDialog(false);
      setCancelReason('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      pending: { variant: 'warning', label: 'Pending' },
      active: { variant: 'success', label: 'Active' },
      fraud: { variant: 'danger', label: 'Fraud' },
      cancelled: { variant: 'default', label: 'Cancelled' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPaymentBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      unpaid: { variant: 'warning', label: 'Unpaid' },
      paid: { variant: 'success', label: 'Paid' },
      refunded: { variant: 'default', label: 'Refunded' },
    };
    const config = statusMap[status] || statusMap.unpaid;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8 text-center">
          <p className="text-slate-400">Order not found</p>
          <button
            onClick={() => navigate('/admin/orders')}
            className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/orders')}
          className="text-orange-400 hover:text-orange-300 mb-4 inline-flex items-center"
        >
          ← Back to Orders
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-100">Order #{order.orderNumber}</h1>
            <p className="text-slate-400 mt-1">
              Placed on {new Date(order.createdAt).toLocaleDateString()} at{' '}
              {new Date(order.createdAt).toLocaleTimeString()}
            </p>
          </div>
          <div className="flex gap-2">
            {getStatusBadge(order.status)}
            {getPaymentBadge(order.paymentStatus)}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Fraud Alert */}
      {order.status === 'fraud' && (
        <div className="mb-6 bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-red-400 font-semibold mb-2">⚠️ Fraud Alert</h3>
              <p className="text-sm text-red-300 mb-1">
                Fraud Score: {order.fraudCheckScore}/100
              </p>
              <p className="text-sm text-slate-400">
                This order has been flagged for potential fraud. Review carefully before accepting.
              </p>
            </div>
            <button
              onClick={handleAcceptFraudOrder}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {actionLoading ? 'Processing...' : 'Accept Order'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Order Items</h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="bg-gray-900 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-100">{item.productName}</h3>
                      <p className="text-sm text-slate-400">
                        {item.billingCycle} × {item.quantity}
                      </p>
                      {item.domain && (
                        <p className="text-sm text-slate-500">Domain: {item.domain}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-100">{formatPrice(item.total)}</p>
                      {item.setupFee > 0 && (
                        <p className="text-xs text-slate-400">+{formatPrice(item.setupFee)} setup</p>
                      )}
                    </div>
                  </div>
                  {item.configurableOptions && Object.keys(item.configurableOptions).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-700/50">
                      <p className="text-xs text-slate-500 mb-1">Configuration:</p>
                      {Object.entries(item.configurableOptions).map(([key, value]) => (
                        <p key={key} className="text-xs text-slate-400">
                          {key}: {String(value)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="mt-6 pt-6 border-t border-slate-700/50">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Subtotal</span>
                  <span>{formatPrice(order.subtotal)}</span>
                </div>
                {order.setupFeeTotal > 0 && (
                  <div className="flex justify-between text-slate-300">
                    <span>Setup Fees</span>
                    <span>{formatPrice(order.setupFeeTotal)}</span>
                  </div>
                )}
                {order.discount > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>
                      Discount
                      {order.promoCode && <span className="ml-1">({order.promoCode})</span>}
                    </span>
                    <span>-{formatPrice(order.discount)}</span>
                  </div>
                )}
                {order.taxAmount > 0 && (
                  <div className="flex justify-between text-slate-300">
                    <span>Tax ({(order.taxRate * 100).toFixed(1)}%)</span>
                    <span>{formatPrice(order.taxAmount)}</span>
                  </div>
                )}
                <div className="border-t border-slate-700/50 pt-2">
                  <div className="flex justify-between text-lg font-semibold text-slate-100">
                    <span>Total</span>
                    <span>{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {(order.notes || order.adminNotes) && (
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4">Notes</h2>
              {order.notes && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-slate-400 mb-1">Client Notes:</h3>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{order.notes}</p>
                </div>
              )}
              {order.adminNotes && (
                <div>
                  <h3 className="text-sm font-medium text-slate-400 mb-1">Admin Notes:</h3>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{order.adminNotes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Client Information */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Client Information</h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-400">Name</p>
                <p className="text-slate-100 font-medium">
                  {order.firstName} {order.lastName}
                </p>
              </div>
              {order.companyName && (
                <div>
                  <p className="text-slate-400">Company</p>
                  <p className="text-slate-100">{order.companyName}</p>
                </div>
              )}
              <div>
                <p className="text-slate-400">Email</p>
                <p className="text-slate-100">{order.email}</p>
              </div>
              {order.phoneNumber && (
                <div>
                  <p className="text-slate-400">Phone</p>
                  <p className="text-slate-100">{order.phoneNumber}</p>
                </div>
              )}
              <div>
                <p className="text-slate-400">IP Address</p>
                <p className="text-slate-100 font-mono text-xs">{order.ipAddress}</p>
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Billing Address</h2>
            <div className="text-slate-300 text-sm space-y-1">
              <p>{order.address1}</p>
              {order.address2 && <p>{order.address2}</p>}
              <p>
                {order.city}, {order.state} {order.postcode}
              </p>
              <p>{order.country}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <h2 className="text-xl font-semibold text-slate-100 mb-4">Actions</h2>
            <div className="space-y-3">
              <button
                onClick={() => setShowStatusUpdate(!showStatusUpdate)}
                className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Update Status
              </button>

              {order.status !== 'cancelled' && (
                <button
                  onClick={() => setShowCancelDialog(true)}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Cancel Order
                </button>
              )}
            </div>

            {/* Status Update Form */}
            {showStatusUpdate && (
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <label className="block text-sm text-slate-400 mb-1">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 mb-3 bg-gray-900 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="fraud">Fraud</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                <label className="block text-sm text-slate-400 mb-1">Admin Notes (Optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 mb-3 bg-gray-900 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Reason for status change..."
                />

                <button
                  onClick={handleUpdateStatus}
                  disabled={actionLoading || newStatus === order.status}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Updating...' : 'Save Status'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-slate-100 mb-4">Cancel Order</h3>
            <p className="text-slate-400 text-sm mb-4">
              Please provide a reason for cancelling this order.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 mb-4 bg-gray-900 border border-slate-700/50 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Cancellation reason..."
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelReason('');
                }}
                className="flex-1 px-4 py-2 bg-slate-700/50 text-slate-200 rounded-lg hover:bg-slate-600/50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={actionLoading || !cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
